import { useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { useTranslation } from "@/lib/i18n";
import { usePostHog } from "@posthog/react";

interface FAQSectionProps {
  panelEyebrow?: string;
  panelHeading?: string;
  panelBody?: string;
  panelCta?: string;
  panelHref?: string;
}

const FAQSection = ({ panelEyebrow, panelHeading, panelBody, panelCta, panelHref = "#book" }: FAQSectionProps) => {
  const [search, setSearch] = useState("");
  const prefersReduced = useReducedMotion();
  const t = useTranslation();
  const f = t.aiTeams.faq;
  const posthog = usePostHog();

  const anim = {
    initial: prefersReduced ? { opacity: 0 } : { opacity: 0, y: 30 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true, margin: "-80px" as const },
    transition: { duration: 0.6, ease: [0.16, 1, 0.3, 1] as const },
  };

  const filtered = f.items.filter(
    (faq) =>
      faq.q.toLowerCase().includes(search.toLowerCase()) ||
      faq.a.toLowerCase().includes(search.toLowerCase())
  );

  const hasPanel = !!(panelEyebrow && panelHeading && panelBody && panelCta);

  return (
    <section id="faq" className="py-12 bg-card/40">
      <div className="container mx-auto px-6 max-w-6xl">
        <div className={hasPanel ? "grid lg:grid-cols-[1fr_1fr] gap-12" : ""}>
          {/* Left: FAQ content */}
          <div>
            <motion.div
              initial={anim.initial}
              whileInView={anim.whileInView}
              viewport={anim.viewport}
              transition={anim.transition}
              className="mb-12"
            >
              <p className="text-xs font-body font-semibold tracking-[0.2em] uppercase text-primary mb-3">{f.eyebrow}</p>
              <h2 className="text-4xl sm:text-5xl font-display tracking-tight">{f.heading}</h2>
            </motion.div>

            <div className={hasPanel ? "" : "max-w-2xl"}>
              {filtered.length === 0 ? (
                <p className="text-sm font-body text-muted-foreground">
                  No matching questions.{" "}
                  <a
                    href="https://www.linkedin.com/in/krzysztofscibiorek"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary hover:underline"
                  >
                    Feel free to ask me directly.
                  </a>
                </p>
              ) : (
                <Accordion type="single" collapsible className="space-y-3">
                  {filtered.map((faq, i) => (
                    <AccordionItem
                      key={i}
                      value={`faq-${i}`}
                      className="rounded-xl border border-border bg-card px-6 data-[state=open]:border-primary/30 card-hover hover:border-l-2 hover:border-l-primary/40 transition-all"
                    >
                      <AccordionTrigger className="text-sm font-body font-medium hover:no-underline py-5">
                        {faq.q}
                      </AccordionTrigger>
                      <AccordionContent className="text-sm font-body text-muted-foreground leading-relaxed pb-5">
                        {faq.a}
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              )}
            </div>
          </div>

          {/* Right: sticky CTA panel (optional) */}
          {hasPanel && (
            <motion.div
              initial={anim.initial}
              whileInView={anim.whileInView}
              viewport={anim.viewport}
              transition={{ delay: 0.15, ...anim.transition }}
              className="h-fit lg:sticky lg:top-24 lg:mt-[110px]"
            >
              <div className="rounded-2xl border border-border bg-card/40 p-8">
                <p className="text-xs font-body font-semibold tracking-[0.2em] uppercase text-accent mb-3">
                  <span
                    className="inline-block mr-1.5"
                    style={!prefersReduced ? { animation: "pulse-dot 2s ease-in-out infinite" } : undefined}
                  >
                    ●
                  </span>
                  {panelEyebrow}
                </p>
                <h3 className="text-xl font-display text-foreground mb-3">
                  {panelHeading}
                </h3>
                <p className="text-sm font-body text-muted-foreground leading-relaxed mb-6">
                  {panelBody}
                </p>
                <a
                  href={panelHref}
                  onClick={() => posthog?.capture("faq_panel_cta_clicked", { cta_label: panelCta })}
                  className="inline-flex items-center justify-center rounded-lg bg-primary text-primary-foreground font-body font-semibold text-sm px-6 py-3 min-h-[44px] hover:bg-primary/90 transition-colors"
                >
                  {panelCta}
                </a>
              </div>
            </motion.div>
          )}
        </div>
      </div>
    </section>
  );
};

export default FAQSection;
